import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Modal, Button, Progress } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { logoutUser } from '../../store/redux/actions/userActions';
import { getSessionData } from '../../utils/interceptors/localStorageService';
import { SelfUserClassificationEnum } from '../../shared/constants/constants';
import { savePendingEntityProcessorRoute } from '../../utils/helper/entityProcessorRedirect';

/**
 * InactivityModal Component
 * 
 * Monitors user activity and shows warning modal after inactivity
 * Auto-logout if user doesn't respond within countdown period
 * 
 * Timings (TESTING MODE):
 * - Inactivity period: 30 seconds
 * - Warning countdown: 10 seconds
 * - Total idle time before logout: 40 seconds
 * 
 * Production Timings (to restore later):
 * - Inactivity period: 5 minutes (300 seconds)
 * - Warning countdown: 60 seconds
 * - Total idle time before logout: 6 minutes
 */
const InactivityModal = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  
  // Configuration - PRODUCTION MODE
  const INACTIVITY_TIMEOUT = 5 * 60 * 1000; // 5 minutes (300 seconds)
  const WARNING_COUNTDOWN = 60; // 60 seconds countdown
  
  // Routes where inactivity monitoring should NOT run (authentication/public pages)
  const EXCLUDED_ROUTES = [
    '/',
    '/landing',
    '/otp',
    '/forgot-password',
    '/reset-password',
    '/validate-user',
    '/login',
    '/signup',
    '/segment',
    '/authentication',
    '/admin/login',
    '/sso-login',
    '/admin/sso-login',
    '/auth/callback',
    '/contact',
    '/terms',
    '/privacy',
    '/company-profile',
    '/request-approval',
    '/test/request-approval',
  ];
  
  // State
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [countdown, setCountdown] = useState(WARNING_COUNTDOWN);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  
  // Refs for timers
  const inactivityTimerRef = useRef(null);
  const countdownTimerRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  
  // Check if current route should be monitored
  const shouldMonitor = useCallback(() => {
    const path = location.pathname;
    const isExcluded = EXCLUDED_ROUTES.some(
      (route) => path === route || path.startsWith(`${route}/`)
    );
    const sessionData = getSessionData();
    const hasToken = !!sessionData?.accessToken;
    
    return hasToken && !isExcluded;
  }, [location.pathname]);
  
  // Check if user is logged in and on a protected route
  useEffect(() => {
    const loggedIn = shouldMonitor();
    setIsLoggedIn(loggedIn);
  }, [shouldMonitor]);
  
  /**
   * Handle user logout
   */
  const handleLogout = useCallback(() => {
    console.log('⏰ Auto-logout due to inactivity');
    
    const sessionData = getSessionData();
    const classification = sessionData?.userClassification;

    // Keep the current route and its query/hash UI state across inactivity logout.
    savePendingEntityProcessorRoute(
      `${window.location.pathname}${window.location.search}${window.location.hash}`,
    );
    
    // Clear modal state
    setIsModalVisible(false);
    setCountdown(WARNING_COUNTDOWN);
    
    // Clear all timers
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
    }
    
    // Dispatch logout action
    dispatch(logoutUser());
    
    // Redirect to appropriate login page
    if (
      classification ===
      SelfUserClassificationEnum.BACKOFFICE_ADMINISTRATORS.value
    ) {
      navigate('/authentication/admin/login');
    } else {
      navigate('/authentication/login');
    }
  }, [dispatch, navigate]);
  
  /**
   * Start countdown timer (60 seconds)
   */
  const startCountdown = useCallback(() => {
    console.log('⏰ Starting inactivity countdown');
    
    // Clear any existing countdown
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    
    setCountdown(WARNING_COUNTDOWN);
    
    const intervalId = setInterval(() => {
      setCountdown((prev) => {
        const next = prev - 1;
        if (next <= 0) {
          clearInterval(intervalId);
          handleLogout();
          return 0;
        }
        return next;
      });
    }, 1000); // Update every second
    
    countdownTimerRef.current = intervalId;
  }, [handleLogout]);
  
  /**
   * Show inactivity warning modal
   */
  const showInactivityModal = useCallback(() => {
    console.log('⚠️ Showing inactivity warning modal');
    setIsModalVisible(true);
    startCountdown();
  }, [startCountdown]);
  
  /**
   * Reset inactivity timer
   */
  const resetInactivityTimer = useCallback(() => {
    const now = Date.now();
    lastActivityRef.current = now;
    
    // Clear existing timers
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
    }
    
    // Hide modal if it's showing
    if (isModalVisible) {
      setIsModalVisible(false);
      setCountdown(WARNING_COUNTDOWN);
    }
    
    // Start new inactivity timer
    if (isLoggedIn) {
      inactivityTimerRef.current = setTimeout(() => {
        showInactivityModal();
      }, INACTIVITY_TIMEOUT);
    }
  }, [isModalVisible, isLoggedIn, showInactivityModal]);
  
  /**
   * Handle "I'm still here" button click
   */
  const handleStillHere = () => {
    console.log('✅ User confirmed activity');
    resetInactivityTimer();
  };
  
  /**
   * Handle manual logout button click
   */
  const handleLogoutClick = () => {
    handleLogout();
  };
  
  /**
   * Setup activity listeners
   */
  useEffect(() => {
    if (!isLoggedIn) {
      return;
    }
    
    // Don't listen to activity events when modal is visible
    // User must explicitly click "I'm still here" button
    if (isModalVisible) {
      return;
    }
    
    // Events to track user activity
    const activityEvents = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
    ];
    
    // Throttle activity reset to avoid too many calls
    let throttleTimeout = null;
    const throttledReset = () => {
      if (!throttleTimeout) {
        throttleTimeout = setTimeout(() => {
          resetInactivityTimer();
          throttleTimeout = null;
        }, 1000); // Throttle to once per second
      }
    };
    
    // Add event listeners
    activityEvents.forEach((event) => {
      window.addEventListener(event, throttledReset, true);
    });
    
    // Start initial timer
    resetInactivityTimer();
    
    // Cleanup - DON'T clear countdown timer here as it's managed separately
    return () => {
      activityEvents.forEach((event) => {
        window.removeEventListener(event, throttledReset, true);
      });
      
      // Only clear the inactivity timer, not the countdown timer
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
      
      if (throttleTimeout) {
        clearTimeout(throttleTimeout);
      }
    };
  }, [isLoggedIn, isModalVisible, resetInactivityTimer]);
  
  // Calculate progress percentage for visual countdown
  const progressPercent = ((WARNING_COUNTDOWN - countdown) / WARNING_COUNTDOWN) * 100;
  
  // Don't render anything if user is not logged in
  if (!isLoggedIn) {
    return null;
  }
  
  return (
    <>
      <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <WarningOutlined style={{ color: '#faad14', fontSize: '24px' }} />
          <span>Are you still there?</span>
        </div>
      }
      open={isModalVisible}
      closable={false}
      maskClosable={false}
      keyboard={false}
      zIndex={10000}
      footer={[
        <Button key="logout" onClick={handleLogoutClick}>
          Logout Now
        </Button>,
        <Button key="stay" type="primary" onClick={handleStillHere}>
          Yes, I'm Still Here
        </Button>,
      ]}
      width={480}
      centered
    >
      <div style={{ textAlign: 'center', padding: '20px 0' }}>
        <p style={{ fontSize: '16px', marginBottom: '24px' }}>
          You've been inactive for a while. For your security, you'll be automatically logged out in:
        </p>
        
        <div style={{ marginBottom: '24px' }}>
          <div style={{ 
            fontSize: '48px', 
            fontWeight: 'bold', 
            color: countdown <= 30 ? '#ff4d4f' : '#1890ff',
            marginBottom: '12px'
          }}>
            {countdown}s
          </div>
          
          <Progress
            percent={progressPercent}
            strokeColor={{
              '0%': '#108ee9',
              '100%': countdown <= 30 ? '#ff4d4f' : '#87d068',
            }}
            showInfo={false}
            size="small"
          />
        </div>
        
        <p style={{ fontSize: '14px', color: '#8c8c8c' }}>
          Click "Yes, I'm Still Here" to continue your session
        </p>
      </div>
    </Modal>
    </>
  );
};

export default InactivityModal;
